"use client";

import { useCallback, useEffect, useRef } from "react";
import type { DashboardWeekTab } from "@/libs/tracking/dashboardWeeks";

interface DashboardWeekTabsProps {
  weeks: DashboardWeekTab[];
  activeId: string;
  onChange: (id: string) => void;
}

function ScrollArrow({
  direction,
  onClick,
}: {
  direction: "left" | "right";
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={direction === "left" ? "Scroll weeks left" : "Scroll weeks right"}
      className="grid h-8 w-8 shrink-0 place-items-center text-rose-deep transition hover:text-rose"
    >
      <svg
        className="h-5 w-5"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {direction === "left" ? (
          <path d="m15 18-6-6 6-6" />
        ) : (
          <path d="m9 18 6-6-6-6" />
        )}
      </svg>
    </button>
  );
}

function PendingSpinner() {
  return (
    <span className="grid h-3.5 w-3.5 shrink-0 place-items-center text-rose" aria-hidden>
      <svg
        className="h-3 w-3 animate-spin"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
      >
        <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
      </svg>
    </span>
  );
}

function WeekPillLabel({ week }: { week: DashboardWeekTab }) {
  if (week.kind === "baseline") {
    return (
      <span className="flex items-center gap-1.5">
        <span>{week.label}</span>
        <span className="rounded-md border border-rose/25 bg-rose-softer/80 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-rose-deep">
          Baseline
        </span>
      </span>
    );
  }

  if (week.kind === "pending") {
    return (
      <span className="flex items-center gap-1.5">
        <span>{week.label}</span>
        <PendingSpinner />
      </span>
    );
  }

  return <span>{week.label}</span>;
}

export default function DashboardWeekTabs({
  weeks,
  activeId,
  onChange,
}: DashboardWeekTabsProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  const scroll = useCallback((direction: "left" | "right") => {
    const el = scrollRef.current;
    if (!el) return;
    const amount = Math.max(180, Math.floor(el.clientWidth * 0.7));
    el.scrollBy({ left: direction === "left" ? -amount : amount, behavior: "smooth" });
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const activeBtn = el.querySelector<HTMLButtonElement>('button[data-active="true"]');
    activeBtn?.scrollIntoView({ behavior: "smooth", inline: "nearest", block: "nearest" });
  }, [activeId, weeks]);

  if (weeks.length === 0) return null;

  return (
    <div className="mt-3 flex w-full items-center gap-1 sm:gap-2">
      <ScrollArrow direction="left" onClick={() => scroll("left")} />

      <div ref={scrollRef} className="no-bar flex min-w-0 flex-1 gap-1.5 overflow-x-auto py-0.5">
        {weeks.map((week) => {
          const isActive = week.id === activeId;
          const isBaseline = week.kind === "baseline";
          const isPending = week.kind === "pending";

          return (
            <button
              key={week.id}
              type="button"
              data-active={isActive ? "true" : "false"}
              onClick={() => onChange(week.id)}
              className={[
                "section-pill shrink-0 rounded-xl px-3 py-2 text-[12.5px] font-semibold text-muted transition sm:px-3.5 sm:text-[13px]",
                isBaseline && !isActive ? "" : "",
                isPending && !isActive ? "opacity-75" : "",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              <WeekPillLabel week={week} />
            </button>
          );
        })}
      </div>

      <ScrollArrow direction="right" onClick={() => scroll("right")} />
    </div>
  );
}
